import React from 'react'
import { Sidebar, SidebarBody, SidebarLink, SidebarLogo, SidebarSection, type SidebarLinkItem } from '@/shared/ui/Sidebar'
import { useNavLinks } from '../hooks/useNavLinks'
import { useNavLayout } from '../hooks/useNavLayout'
import TopNav from './TopNav'
import UserMenu from './UserMenu'
import { MobileBottomNav } from '@/shared/ui/MobileBottomNav'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useSidebar } from '@/shared/ui/Sidebar'
import PlanExpiredNoticeGate from '@/modules/plans/components/PlanExpiredNoticeGate'

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { links: primaryLinks, effectiveRole } = useNavLinks()
  const { layout, ready } = useNavLayout()
  const { user, profile } = useAuth()

  const links: SidebarLinkItem[] = primaryLinks

  if (!ready) return null
  if (layout === 'header') {
    return (
      <div className="min-h-screen w-full bg-background text-foreground">
        <TopNav />
        <main className="flex h-full flex-1">
          <div className="flex w-full flex-1 flex-col gap-2 bg-background p-4 pb-24 md:p-8 md:pb-8">
            <div className="mx-auto w-full max-w-[1550px]">{children}</div>
          </div>
        </main>
        {/* Bottom nav apenas mobile */}
        <MobileBottomNav />
        <PlanExpiredNoticeGate />
      </div>
    )
  }
  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <div className="flex min-h-screen w-full flex-1 flex-col md:flex-row">
        <Sidebar>
          {/* Sem header mobile para master/afiliado; navegacao fica no menu bottom */}
          <SidebarBody showMobileHeader={false} className="justify-between gap-6 p-4">
            <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto pb-20">
              <SidebarLogo />
              <div className="mt-6">
                <SidebarSection>
                  {links.map((link, idx) => (
                    <SidebarLink key={idx} link={link} />
                  ))}
                </SidebarSection>
              </div>
            </div>
            <div className="sticky bottom-3 flex items-center gap-2 p-1 bg-muted/30 border-t border-border">
              <UserMenu side="up" trigger={
                <div className="flex cursor-pointer items-center gap-2">
                  <img src={profile?.avatar_url || ''} onError={(e:any)=>{ e.currentTarget.src='' }} alt="avatar" className="h-9 w-9 flex-shrink-0 rounded-full border border-border object-cover" />
                  <SidebarUserInfo name={profile?.full_name || user?.email || ''} email={user?.email || ''} />
                  <SidebarChevron />
                </div>
              }
              />
            </div>
          </SidebarBody>
        </Sidebar>

        <main className="flex h-full flex-1">
          <div className="flex w-full flex-1 flex-col gap-2 border-l border-border bg-background p-4 pb-24 md:p-8 md:pb-8">
            <div className="mx-auto w-full max-w-[1550px]">{children}</div>
          </div>
        </main>
      </div>
      {/* Bottom nav apenas mobile */}
      <MobileBottomNav />
      <PlanExpiredNoticeGate />
    </div>
  )
}

function SidebarUserInfo({ name, email }: { name: string; email: string }) {
  const { open } = useSidebar()
  if (!open) return null
  if (!name && !email) return null
  return (
    <div className="hidden md:block">
      {name && <div className="text-sm font-medium leading-tight">{name}</div>}
      {email && <div className="text-xs text-foreground/70 leading-tight">{email}</div>}
    </div>
  )
}

function SidebarChevron() {
  const { open } = useSidebar()
  if (!open) return null
  return <ChevronDown className="h-4 w-4 text-foreground/70" />
}
